// Gamification badge engine — pure and shared between the Worker and the
// React client so the same rules power the Collection shelf and the public
// shared-trip page. No I/O, no framework: give it a snapshot, get badges.
//
// Two kinds of badge:
//   • SET badges (family: group | endemic | rarity | signature) map to a
//     specific set of species. `memberTaxonIds` lists which of the snapshot's
//     species count — that's what lets the share page cluster photos by badge.
//   • ACCOUNT badges (family: milestone | place | trip | diversity) are about
//     the whole lifelist / trip history and don't map to individual species.
//
// A species can belong to several sets; the share page picks the "best" group
// (closest-to-complete) so each photo appears once. See computeShareGroups.

import { normName } from "./species";
import {
  ENDEMIC_REGIONS,
  detectRegions,
  scoreEndemics,
  endemicRegionsForName,
} from "./endemics";

// Rarity thresholds mirror src/client/lib/rarity.ts. Kept as literals here so
// the shared engine has no client dependency; if the client's thresholds move,
// move these too (there's a unit-free guard test-of-eye: Rare == count < 10).
const RARE_BELOW = 10; // observation_count < 10 → Rare

export type BadgeFamily =
  | "milestone"
  | "group"
  | "endemic"
  | "rarity"
  | "place"
  | "trip"
  | "signature"
  | "diversity";

export interface BadgeSpecies {
  taxon_id: number;
  scientific_name: string;
  common_name?: string;
  category?: string;
  observation_count?: number;
  location_name?: string;
}

export interface ComputedBadge {
  id: string;
  family: BadgeFamily;
  title: string;
  /** Short caption under the title, e.g. "8 of 22" or "100 species". */
  caption: string;
  icon: string; // emoji
  /** Accent hex for the progress ring + icon chip. */
  color: string;
  have: number;
  need: number;
  pct: number; // 0..100, clamped
  earned: boolean;
  /** Snapshot species that count toward this badge (SET badges only). */
  memberTaxonIds: number[];
  /** True for the top rung of a tiered family — gets a subtle glow. */
  capstone?: boolean;
}

export interface BadgeSnapshot {
  species: BadgeSpecies[];
  /** Trip history — account badges only. Omit on the share page. */
  trips?: { beaches?: string[]; created_at: string; species_count?: number }[];
  /** Distinct beach names visited — for the Explorer badge. */
  beaches?: string[];
  /** Extra region hints (beach regions, spot names) for endemic detection. */
  regionHints?: string[];
}

// ---------- helpers ----------

const clampPct = (have: number, need: number) =>
  need <= 0 ? 0 : Math.max(0, Math.min(100, Math.round((have / need) * 100)));

function haystack(sp: BadgeSpecies): string {
  return `${sp.common_name ?? ""} ${sp.scientific_name}`.toLowerCase();
}

function distinct(species: BadgeSpecies[]): BadgeSpecies[] {
  const seen = new Set<number>();
  const out: BadgeSpecies[] = [];
  for (const s of species) {
    if (seen.has(s.taxon_id)) continue;
    seen.add(s.taxon_id);
    out.push(s);
  }
  return out;
}

// ---------- group specialists ----------

interface GroupDef {
  key: string;
  title: string;
  icon: string;
  color: string;
  need: number;
  match: (sp: BadgeSpecies) => boolean;
}

// Name-based matchers. "ray" would catch "moray", so rays explicitly exclude
// morays; eels claim the morays. Order-independent — each species is tested
// against every group and can count toward more than one.
const GROUPS: GroupDef[] = [
  {
    key: "sharks",
    title: "Shark Spotter",
    icon: "🦈",
    color: "#3f6d86",
    need: 3,
    match: (s) => haystack(s).includes("shark"),
  },
  {
    key: "rays",
    title: "Ray Rider",
    icon: "🐟",
    color: "#5a7d9a",
    need: 3,
    match: (s) => {
      const h = haystack(s);
      if (h.includes("moray")) return false;
      return h.includes("ray") || h.includes("manta") || h.includes("skate");
    },
  },
  {
    key: "turtles",
    title: "Turtle Tracker",
    icon: "🐢",
    color: "#2f8f6b",
    need: 3,
    match: (s) => haystack(s).includes("turtle"),
  },
  {
    key: "eels",
    title: "Eel Wrangler",
    icon: "🪱",
    color: "#7a6a3f",
    need: 3,
    match: (s) => {
      const h = haystack(s);
      return h.includes("eel") || h.includes("moray");
    },
  },
  {
    key: "cephalopods",
    title: "Ink Hunter",
    icon: "🐙",
    color: "#8a5a86",
    need: 2,
    match: (s) => {
      const h = haystack(s);
      return (
        h.includes("octopus") ||
        h.includes("squid") ||
        h.includes("cuttlefish") ||
        h.includes("nautilus")
      );
    },
  },
  {
    key: "puffers",
    title: "Puffer Patrol",
    icon: "🐡",
    color: "#c98a12",
    need: 3,
    match: (s) => {
      const h = haystack(s);
      return (
        h.includes("puffer") ||
        h.includes("toby") ||
        h.includes("porcupinefish") ||
        h.includes("burrfish")
      );
    },
  },
  {
    key: "wrasses",
    title: "Wrasse Whisperer",
    icon: "🐠",
    color: "#0e7c86",
    need: 5,
    match: (s) => haystack(s).includes("wrasse"),
  },
  {
    key: "butterflyfish",
    title: "Butterfly Collector",
    icon: "🦋",
    color: "#d98324",
    need: 5,
    match: (s) => haystack(s).includes("butterflyfish"),
  },
  {
    key: "parrotfish",
    title: "Parrot Party",
    icon: "🦜",
    color: "#2f9e7d",
    need: 3,
    match: (s) => haystack(s).includes("parrotfish"),
  },
  {
    key: "triggerfish",
    title: "Trigger Happy",
    icon: "🎯",
    color: "#b05a3c",
    need: 3,
    match: (s) => haystack(s).includes("triggerfish"),
  },
  {
    key: "angelfish",
    title: "Angel Aura",
    icon: "😇",
    color: "#c17ba0",
    need: 3,
    match: (s) => haystack(s).includes("angelfish"),
  },
  {
    key: "nudibranchs",
    title: "Nudi Nerd",
    icon: "🌈",
    color: "#c74f9a",
    need: 3,
    match: (s) => {
      const h = haystack(s);
      return (
        h.includes("nudibranch") ||
        h.includes("sea slug") ||
        h.includes("sea hare")
      );
    },
  },
];

// ---------- signature "bingo" sets ----------

export interface SignatureSet {
  key: string;
  title: string;
  icon: string;
  color: string;
  /**
   * Destination gate. A place-specific set only surfaces when its region is
   * detected from the snapshot's locations/beaches — otherwise a single
   * wide-ranging member (a manta, a green turtle) would light up "Maldives
   * Highlights" on a Hawaii trip. Omit for genuinely global sets (Big
   * Pelagics), which surface anywhere once you've bagged one. As a fallback,
   * a gated set still reveals if you're already ≥60% through it.
   */
  region?: string;
  /** Scientific names (matched via normName). */
  members: string[];
}

// Local matchers for signature-set destinations. Endemics.ts owns the ones
// that also have endemic checklists (hawaii); these add Indo-Pacific hotspots
// that are icon-sets only.
const SIGNATURE_REGION_MATCHERS: Record<string, RegExp> = {
  hawaii:
    /hawaii|hawaiʻi|maui|oʻahu|oahu|kauaʻi|kauai|big\s?island|kona|hilo|kohala|molokini|hanauma|napali|kealakekua|molokai|lanai/i,
  maldives: /maldives|mald[ií]v|ari atoll|baa atoll|male\b|malé/i,
  gbr: /great barrier reef|\bgbr\b|cairns|whitsundays|port douglas|coral sea|lizard island|queensland/i,
  coraltriangle:
    /raja ampat|komodo|wakatobi|lembeh|bunaken|misool|sulawesi|philippines|palawan|anilao|tubbataha|coral triangle/i,
};

function detectSignatureRegions(texts: string[]): Set<string> {
  const hay = texts.join(" | ");
  const out = new Set<string>();
  for (const [k, re] of Object.entries(SIGNATURE_REGION_MATCHERS)) {
    if (re.test(hay)) out.add(k);
  }
  return out;
}

// Region-agnostic + destination icon sets. Honest framing: these are "icons,"
// not endemics, so wide-ranging headliners are fine here.
export const SIGNATURE_SETS: SignatureSet[] = [
  {
    key: "hawaii-icons",
    title: "Hawaii Reef Icons",
    icon: "🌺",
    color: "#e0603a",
    region: "hawaii",
    members: [
      "Zebrasoma flavescens", // Yellow Tang
      "Rhinecanthus rectangulus", // Reef Triggerfish (humuhumu)
      "Zanclus cornutus", // Moorish Idol
      "Chelonia mydas", // Green Sea Turtle
      "Mobula alfredi", // Reef Manta
      "Stenella longirostris", // Spinner Dolphin
    ],
  },
  {
    key: "big-pelagics",
    title: "Big Pelagics",
    icon: "🌊",
    color: "#0a5a63",
    members: [
      "Rhincodon typus", // Whale Shark
      "Mobula birostris", // Giant Manta
      "Aetobatus narinari", // Spotted Eagle Ray
      "Chelonia mydas", // Green Sea Turtle
      "Carcharhinus melanopterus", // Blacktip Reef Shark
      "Tursiops truncatus", // Bottlenose Dolphin
    ],
  },
  {
    key: "maldives-highlights",
    title: "Maldives Highlights",
    icon: "🏝️",
    color: "#1f8fa8",
    region: "maldives",
    members: [
      "Rhincodon typus", // Whale Shark
      "Mobula alfredi", // Reef Manta
      "Cheilinus undulatus", // Napoleon Wrasse
      "Acanthurus leucosternon", // Powder Blue Tang
      "Plectorhinchus vittatus", // Oriental Sweetlips
      "Carcharhinus melanopterus", // Blacktip Reef Shark
    ],
  },
  {
    key: "coral-triangle",
    title: "Coral Triangle Icons",
    icon: "🪸",
    color: "#c74f6a",
    region: "coraltriangle",
    members: [
      "Synchiropus splendidus", // Mandarinfish
      "Amphiprion ocellaris", // Clown Anemonefish
      "Cheilinus undulatus", // Napoleon Wrasse
      "Mobula alfredi", // Reef Manta
      "Hippocampus bargibanti", // Pygmy Seahorse
      "Eucrossorhinus dasypogon", // Tasselled Wobbegong
    ],
  },
  {
    key: "gbr-icons",
    title: "Great Barrier Reef Icons",
    icon: "🐠",
    color: "#2f9e7d",
    region: "gbr",
    members: [
      "Epinephelus tukula", // Potato Cod
      "Cheilinus undulatus", // Napoleon Wrasse
      "Chelonia mydas", // Green Sea Turtle
      "Tridacna gigas", // Giant Clam
      "Amphiprion percula", // Clown Anemonefish
      "Mobula alfredi", // Reef Manta
    ],
  },
];

// ---------- milestone / rarity tiers ----------

const MILESTONES: { n: number; title: string; icon: string }[] = [
  { n: 1, title: "First Fish", icon: "🐟" },
  { n: 10, title: "Schooling", icon: "🎏" },
  { n: 25, title: "Reef Regular", icon: "🪸" },
  { n: 50, title: "Half Century", icon: "🏅" },
  { n: 100, title: "Centurion", icon: "💯" },
  { n: 250, title: "Reef Master", icon: "👑" },
];

const RARITY_TIERS: { n: number; title: string; icon: string }[] = [
  { n: 1, title: "First Rare", icon: "✨" },
  { n: 5, title: "Rare Finder", icon: "💎" },
  { n: 10, title: "Treasure Hunter", icon: "🏆" },
];

const MILESTONE_COLOR = "#0e7c86";
const RARITY_COLOR = "#ef6a4d";
const ENDEMIC_COLOR = "#c98a12";
const PLACE_COLOR = "#3f6d86";
const TRIP_COLOR = "#2f8f6b";
const DIVERSITY_COLOR = "#8a5a86";

// Core categories for the Rainbow badge. Only marine ones snorkelers realistically hit.
const RAINBOW_CATEGORIES = [
  "Fish",
  "Sharks & rays",
  "Molluscs",
  "Reptiles",
  "Crustaceans",
  "Echinoderms",
  "Cnidarians",
  "Mammals",
];

// ---------- the engine ----------

export function computeBadges(snap: BadgeSnapshot): ComputedBadge[] {
  const species = distinct(snap.species);
  const out: ComputedBadge[] = [];

  // --- milestone tiers (account) ---
  const total = species.length;
  MILESTONES.forEach((m, i) => {
    out.push({
      id: `milestone:${m.n}`,
      family: "milestone",
      title: m.title,
      caption: `${m.n} species`,
      icon: m.icon,
      color: MILESTONE_COLOR,
      have: Math.min(total, m.n),
      need: m.n,
      pct: clampPct(total, m.n),
      earned: total >= m.n,
      memberTaxonIds: [],
      capstone: i === MILESTONES.length - 1,
    });
  });

  // --- rarity tiers (set: the Rare species) ---
  const rareIds = species
    .filter((s) => (s.observation_count ?? Infinity) < RARE_BELOW)
    .map((s) => s.taxon_id);
  RARITY_TIERS.forEach((t, i) => {
    out.push({
      id: `rarity:${t.n}`,
      family: "rarity",
      title: t.title,
      caption: `${Math.min(rareIds.length, t.n)} of ${t.n} rare`,
      icon: t.icon,
      color: RARITY_COLOR,
      have: Math.min(rareIds.length, t.n),
      need: t.n,
      pct: clampPct(rareIds.length, t.n),
      earned: rareIds.length >= t.n,
      memberTaxonIds: rareIds,
      capstone: i === RARITY_TIERS.length - 1,
    });
  });

  // --- group specialists (set) — only surface a group once you've started it ---
  for (const g of GROUPS) {
    const members = species.filter(g.match);
    if (members.length === 0) continue;
    out.push({
      id: `group:${g.key}`,
      family: "group",
      title: g.title,
      caption: `${Math.min(members.length, g.need)} of ${g.need}`,
      icon: g.icon,
      color: g.color,
      have: Math.min(members.length, g.need),
      need: g.need,
      pct: clampPct(members.length, g.need),
      earned: members.length >= g.need,
      memberTaxonIds: members.map((s) => s.taxon_id),
    });
  }

  // --- endemic regions (set) — only for detected regions ---
  const locationTexts = [
    ...species.map((s) => s.location_name ?? ""),
    ...(snap.beaches ?? []),
    ...(snap.regionHints ?? []),
  ].filter(Boolean);
  const regionKeys = new Set(detectRegions(locationTexts));
  for (const region of ENDEMIC_REGIONS) {
    if (!regionKeys.has(region.key)) continue;
    const score = scoreEndemics(region, species);
    const seen = score.seen.length;
    const need = region.species.length;
    out.push({
      id: `endemic:${region.key}`,
      family: "endemic",
      title: region.label,
      caption: `${seen} of ${need}`,
      icon: "🎖️",
      color: ENDEMIC_COLOR,
      have: seen,
      need,
      pct: clampPct(seen, need),
      earned: seen >= need,
      memberTaxonIds: [...score.seenTaxonIds.values()],
      capstone: true,
    });
  }

  // --- signature sets (set) — surface once you've bagged at least one ---
  const byNorm = new Map<string, number>();
  for (const s of species) {
    const k = normName(s.scientific_name);
    if (k && !byNorm.has(k)) byNorm.set(k, s.taxon_id);
  }
  const sigRegions = detectSignatureRegions(locationTexts);
  for (const set of SIGNATURE_SETS) {
    const memberIds: number[] = [];
    for (const m of set.members) {
      const id = byNorm.get(normName(m));
      if (id != null) memberIds.push(id);
    }
    if (memberIds.length === 0) continue;
    const pct = clampPct(memberIds.length, set.members.length);
    // Gate destination sets: only surface if that region is detected, or if
    // you're already most of the way through it. Global sets (no region) show
    // anywhere. Prevents a shared manta lighting up far-flung destinations.
    if (set.region && !sigRegions.has(set.region) && pct < 60) continue;
    out.push({
      id: `signature:${set.key}`,
      family: "signature",
      title: set.title,
      caption: `${memberIds.length} of ${set.members.length}`,
      icon: set.icon,
      color: set.color,
      have: memberIds.length,
      need: set.members.length,
      pct,
      earned: memberIds.length >= set.members.length,
      memberTaxonIds: memberIds,
      capstone: true,
    });
  }

  // --- diversity: Rainbow (account) ---
  const cats = new Set(
    species.map((s) => s.category).filter((c): c is string => !!c && c !== "Other"),
  );
  const rainbowHave = RAINBOW_CATEGORIES.filter((c) => cats.has(c)).length;
  out.push({
    id: "diversity:rainbow",
    family: "diversity",
    title: "Full Reef",
    caption: `${rainbowHave} of ${RAINBOW_CATEGORIES.length} groups`,
    icon: "🌈",
    color: DIVERSITY_COLOR,
    have: rainbowHave,
    need: RAINBOW_CATEGORIES.length,
    pct: clampPct(rainbowHave, RAINBOW_CATEGORIES.length),
    earned: rainbowHave >= RAINBOW_CATEGORIES.length,
    memberTaxonIds: [],
  });

  // --- place + trip (account) — only when we have trip data ---
  if (snap.trips) {
    const beaches = snap.beaches ?? [];
    out.push({
      id: "place:explorer",
      family: "place",
      title: "Explorer",
      caption: `${Math.min(beaches.length, 5)} of 5 spots`,
      icon: "🗺️",
      color: PLACE_COLOR,
      have: Math.min(beaches.length, 5),
      need: 5,
      pct: clampPct(beaches.length, 5),
      earned: beaches.length >= 5,
      memberTaxonIds: [],
    });
    const regionCount = regionKeys.size;
    out.push({
      id: "place:globetrotter",
      family: "place",
      title: "Globetrotter",
      caption: `${Math.min(regionCount, 3)} of 3 regions`,
      icon: "🌍",
      color: PLACE_COLOR,
      have: Math.min(regionCount, 3),
      need: 3,
      pct: clampPct(regionCount, 3),
      earned: regionCount >= 3,
      memberTaxonIds: [],
    });

    const tripCount = snap.trips.length;
    out.push({
      id: "trip:first",
      family: "trip",
      title: "First Trip",
      caption: tripCount >= 1 ? "logged" : "not yet",
      icon: "🚩",
      color: TRIP_COLOR,
      have: Math.min(tripCount, 1),
      need: 1,
      pct: clampPct(tripCount, 1),
      earned: tripCount >= 1,
      memberTaxonIds: [],
    });
    out.push({
      id: "trip:frequent",
      family: "trip",
      title: "Frequent Diver",
      caption: `${Math.min(tripCount, 10)} of 10 trips`,
      icon: "🤿",
      color: TRIP_COLOR,
      have: Math.min(tripCount, 10),
      need: 10,
      pct: clampPct(tripCount, 10),
      earned: tripCount >= 10,
      memberTaxonIds: [],
    });
    const bigDay = Math.max(0, ...snap.trips.map((t) => t.species_count ?? 0));
    out.push({
      id: "trip:bigday",
      family: "trip",
      title: "Big Day",
      caption: `${Math.min(bigDay, 20)} of 20 in a trip`,
      icon: "🔥",
      color: TRIP_COLOR,
      have: Math.min(bigDay, 20),
      need: 20,
      pct: clampPct(bigDay, 20),
      earned: bigDay >= 20,
      memberTaxonIds: [],
      capstone: true,
    });
  }

  return out;
}

// Which SET badges a candidate species would contribute to if logged —
// used to rank "haven't seen yet" lists by which badge they move forward.
// Returns computed-badge ids (group:*, endemic:*, signature:*) plus the
// literal "rarity" marker for a rare species (caller resolves the tier).
export function candidateBadgeIds(sp: BadgeSpecies): string[] {
  const ids: string[] = [];
  for (const g of GROUPS) if (g.match(sp)) ids.push(`group:${g.key}`);
  for (const rk of endemicRegionsForName(sp.scientific_name)) {
    ids.push(`endemic:${rk}`);
  }
  const k = normName(sp.scientific_name);
  if (k) {
    for (const set of SIGNATURE_SETS) {
      if (set.members.some((m) => normName(m) === k)) {
        ids.push(`signature:${set.key}`);
      }
    }
  }
  if ((sp.observation_count ?? Infinity) < RARE_BELOW) ids.push("rarity");
  return ids;
}

// The single most-advanced not-yet-earned badge per family, for the
// "closest next" in-progress rail. Tiered families collapse to their next
// rung; set families to their most-complete open set.
export function inProgressBadges(all: ComputedBadge[]): ComputedBadge[] {
  const open = all.filter((b) => !b.earned && b.have > 0);
  const bestPerKey = new Map<string, ComputedBadge>();
  for (const b of open) {
    // Group tiered milestone/rarity by family; keep set badges distinct by id.
    const key =
      b.family === "milestone" || b.family === "rarity"
        ? b.family
        : b.id;
    const cur = bestPerKey.get(key);
    // For tiered families we want the LOWEST unmet tier (smallest need);
    // for sets, the highest pct.
    if (!cur) {
      bestPerKey.set(key, b);
    } else if (b.family === "milestone" || b.family === "rarity") {
      if (b.need < cur.need) bestPerKey.set(key, b);
    } else if (b.pct > cur.pct) {
      bestPerKey.set(key, b);
    }
  }
  return [...bestPerKey.values()].sort((a, b) => b.pct - a.pct);
}

export function earnedBadges(all: ComputedBadge[]): ComputedBadge[] {
  // Tiered families: keep only the highest earned rung so the wall isn't a
  // stack of milestone chips. Set families: keep each earned badge.
  const topTier = new Map<string, ComputedBadge>();
  const rest: ComputedBadge[] = [];
  for (const b of all) {
    if (!b.earned) continue;
    if (b.family === "milestone" || b.family === "rarity") {
      const cur = topTier.get(b.family);
      if (!cur || b.need > cur.need) topTier.set(b.family, b);
    } else {
      rest.push(b);
    }
  }
  return [...topTier.values(), ...rest];
}

// Cluster a species list into badge-set groups for the share page. Each
// species lands in exactly one group — its closest-to-complete set — so
// photos aren't duplicated. Groups are ordered closest-to-complete first;
// anything in no set falls into a trailing null-keyed "Also spotted".
export interface ShareGroup {
  badge: ComputedBadge | null; // null → "Also spotted"
  taxonIds: number[];
}

export function computeShareGroups(
  species: BadgeSpecies[],
  badges: ComputedBadge[],
): ShareGroup[] {
  const setBadges = badges
    .filter((b) => b.memberTaxonIds.length > 0)
    .sort((a, b) => b.pct - a.pct);
  const assigned = new Map<number, ComputedBadge>();
  for (const b of setBadges) {
    for (const id of b.memberTaxonIds) {
      // First (highest-pct) set to claim a species wins.
      if (!assigned.has(id)) assigned.set(id, b);
    }
  }
  const groups: ShareGroup[] = [];
  const order: ComputedBadge[] = [];
  const byBadge = new Map<string, number[]>();
  for (const s of species) {
    const b = assigned.get(s.taxon_id);
    if (!b) continue;
    if (!byBadge.has(b.id)) {
      byBadge.set(b.id, []);
      order.push(b);
    }
    byBadge.get(b.id)!.push(s.taxon_id);
  }
  order.sort((a, b) => b.pct - a.pct);
  for (const b of order) groups.push({ badge: b, taxonIds: byBadge.get(b.id)! });
  const leftover = species
    .filter((s) => !assigned.has(s.taxon_id))
    .map((s) => s.taxon_id);
  if (leftover.length > 0) groups.push({ badge: null, taxonIds: leftover });
  return groups;
}
